import { useCallback, useEffect, useState } from "react";
import { api, act, flash, post } from "../lib/api";
import { dayLabel, useLive } from "../lib/time";
import { PersonCell } from "../components/PersonCell";
import { FloorCard } from "../components/FloorCard";
import { CardSheet } from "../components/CardSheet";
import { LeaveSheet, NewCardSheet } from "../components/FloorSheets";
import { CalendarSheet } from "../components/CalendarSheet";
import { addDays, dayOff } from "../lib/days";

const HL = [["moretime", "Time added"], ["live", "Live"], ["parked", "With client / team"], ["standby", "If any"], ["unplanned", "Unplanned"], ["noest", "No estimate"], ["mine", "Asked by me"]];
function matches(h, t, me) {
  if (h === "live") return !!t.runningSince;
  if (h === "parked") return ["waiting_client", "waiting_internal", "on_hold"].includes(t.state);
  if (h === "standby") return t.kind === "standby";
  if (h === "unplanned") return !!t.unplanned;
  if (h === "noest") return t.estimate?.kind === "open" && t.state !== "done";
  if (h === "moretime") return t.moreTime?.status === "added";
  return (t.pocs || []).includes(me) || t.createdBy === me;
}

function HighlightBar({ tasks, value, onChange, me }) {
  const open = tasks.filter((t) => t.state !== "done");
  return (
    <div className="hlbar" role="group" aria-label="Highlight cards" data-testid="highlight-bar">
      <span className="lbl">Highlight</span>
      {HL.map(([k, l]) => {
        const n = open.filter((t) => matches(k, t, me)).length;
        if ((k === "mine" || k === "moretime") && !n && value !== k) return null;
        return <button key={k} aria-pressed={value === k} className={value === k ? "on" : ""} onClick={() => onChange(value === k ? null : k)} data-testid={`hl-${k}`}>{l}<b className="num">{n}</b></button>;
      })}
    </div>
  );
}

function LeaveStrip({ leaves, rows, date, today, onOpen }) {
  const ids = Object.fromEntries(rows.map((r) => [r.person.id, r.person]));
  const soon = leaves.filter((l) => ids[l.person] && l.to >= date && l.from <= addDays(date, 14)).sort((a, b) => a.from.localeCompare(b.from));
  if (!soon.length) return null;
  return (
    <div className="lstrip" aria-label="People on leave" data-testid="leave-strip">
      <span className="lbl t-warn">✈ On leave, next 2 weeks</span>
      {soon.map((l) => {
        const when = l.from === l.to ? (l.from === today ? "today" : dayLabel(l.from)) : `${l.from <= today ? "till" : `${dayLabel(l.from)} to`} ${dayLabel(l.to)}`;
        return <button key={l.id} onClick={() => onOpen(l.person)} data-testid={`leave-chip-${l.person}`}><b>{ids[l.person].short}</b> <span className="i2">{when}</span></button>;
      })}
    </div>
  );
}

function Legend() {
  return (
    <ul className="legend2" aria-label="Legend" data-testid="tray-legend">
      <li><i className="sw-live" />Live timer</li><li><i className="sw-hatch" />With client / team</li>
      <li><i className="sw-dash" />If any</li><li><i className="sw-unpl" />Unplanned</li>
      <li><b className="t-danger">REV</b>Feedback round</li><li><i className="sw-over" />Over 7h 30m</li>
    </ul>
  );
}

function canMove(me, row) {
  if (me.caps.plan) return true;
  return me.caps.rearrange && row && row.person.team === me.user.team;
}

function DropZone({ onDrop, className, children, testid, enabled }) {
  const [over, setOver] = useState(false);
  if (!enabled) return <div className={className} data-testid={testid}>{children}</div>;
  return (
    <div className={`${className} ${over ? "over" : ""}`} data-testid={testid}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(JSON.parse(e.dataTransfer.getData("text/plain")), null); }}>
      {children}
    </div>
  );
}

function Row({ me, row, names, pmap, drop, openCard, onLeave, onBack, onAdd, spot, hl, isToday }) {
  const movable = canMove(me, row);
  const open = row.tasks.filter((t) => t.state !== "done");
  return (
    <div className="frow" data-testid={`floor-row-${row.person.id}`}>
      <PersonCell row={row} onLeave={onLeave} onBack={onBack} names={names} isToday={isToday} live={row.tasks.find((t) => t.runningSince)} />
      <DropZone className="fstrip" enabled={movable} testid={`floor-strip-${row.person.id}`} onDrop={(c) => drop(c, row.person.id, null)}>
        {row.tasks.length === 0 && !onAdd && <span className="empty">{row.away ? "On leave. Nothing on the list." : "Empty list. Drag a card here."}</span>}
        {row.tasks.map((t) => (
          <FloorCard key={t.id} t={t} names={names} pmap={pmap} dim={(spot && t.client !== spot) || (hl && !matches(hl, t, me.user.id))} draggable={movable && t.state !== "done"} onOpen={() => openCard(t)}
            onDropBefore={movable && t.state !== "done" ? (c) => drop(c, row.person.id, open.indexOf(t)) : null} from={row.person.id} />
        ))}
        {onAdd && !row.away && <button className="add-tile" onClick={onAdd} aria-label={`Add card for ${row.person.short}`}
          data-testid={`add-card-${row.person.id}`}><b>+</b> Add card</button>}
      </DropZone>
    </div>
  );
}

function PlanBar({ floor, date, label, reload, onCalendar, today }) {
  const pub = floor.plan?.published;
  const [cal, setCal] = useState(null);
  useEffect(() => { api("/calendar").then((r) => r.ok && setCal(r.data)); }, [date]);
  const skipped = [];
  if (cal) for (let d = addDays(today, 1); d < date; d = addDays(d, 1)) if (dayOff(d, cal)) skipped.push(dayLabel(d).replace(/ \w+$/, ""));
  return (
    <div className="planbar" data-testid="plan-bar">
      <div className="pb-text">
        <b>Packing {dayLabel(date)}</b>
        {skipped.length > 0 && <span data-testid="plan-skip">No list for {skipped.join(", ")}.</span>}
        <span className={`pub ${pub ? "on" : ""}`} data-testid="plan-status">{pub ? "Published. Everyone already sees their list. Changes still go through live."
          : `Draft. Makers see it the moment you publish.${floor.tray.length ? ` ${floor.tray.length} card(s) still in the tray.` : ""}`}</span>
      </div>
      <button className="btn-ghost sm" onClick={onCalendar} data-testid="working-days-button">Working days</button>
      <button className="btn-ghost sm" onClick={async () => { await act(post("/tasks/carry-over")); reload(); }} data-testid="carry-over-button">
        Carry over unfinished</button>
      <button className={pub ? "btn-ghost sm" : "btn-solid pub-btn"} onClick={async () => { await act(post(`/plans/${date}/${pub ? "unpublish" : "publish"}`)); reload(); }}
        data-testid="publish-toggle">{pub ? "Back to draft" : `Publish ${label}`}</button>
    </div>
  );
}

function FloorStats({ rows, team, isToday, dayWord }) {
  const live = rows.filter((r) => !r.away && r.presence?.kind === "running").length;
  const away = rows.filter((r) => r.away).length;
  const St = ({ v, l, c = "", id }) => <div className="st" data-testid={`stat-${id}`}><b className={c}>{v}</b><span className="lbl">{l}</span></div>;
  return (
    <div className="fstats" data-testid="floor-stats">
      <span className="lbl l3">{team === "video" ? "Video" : "Design"}, {dayWord}</span>
      <St v={rows.length} l={rows.length === 1 ? "person" : "people"} id="people" />
      {isToday && <St v={live} l="live" c="t-live" id="live" />}
      <St v={away} l="on leave" c={away ? "t-warn" : ""} id="leave" />
    </div>
  );
}

// event types that change Floor/Plan rows, cards, presence or the leave strip; resync always reloads (lib/live.js)
const FLOOR_EVENTS = (t) => /^(task|timer|segment|break|person|leave|calendar|day|plan)\./.test(t) || t === "autostop";

export default function Floor({ me, mode, refreshMe }) {
  const [team, setTeam] = useState(me.user.team === "video" ? "video" : "design");
  const [floor, setFloor] = useState(null);
  const [names, setNames] = useState({});
  const [clients, setClients] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [day, setDay] = useState("today");
  const [spot, setSpot] = useState("");
  const [bench, setBench] = useState([]);
  const [pmap, setPmap] = useState({});
  const [hl, setHl] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [trayOpen, setTrayOpen] = useState(true);
  const date = mode === "plan" || day === "next" ? me.tomorrow : me.today;
  const load = useCallback(async (types) => {
    const leavesToo = !types || [...types].some((t) => /^(resync|leave\.|calendar\.|day\.|person\.)/.test(t));
    const [r, l] = await Promise.all([api(`/floor?date=${date}&team=${team}`), leavesToo && api(`/leaves?from=${date}&to=${addDays(date, 14)}`)]);
    if (r.ok) setFloor(r.data);
    if (l?.ok) setLeaves(l.data.items);
  }, [date, team]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api("/clients").then((r) => {
      if (!r.ok) return;
      setClients(r.data.items.filter((c) => c.active));
      setNames(Object.fromEntries(r.data.items.map((c) => [c.code, c.name])));
    });
    api("/people").then((r) => {
      if (!r.ok) return;
      setPmap(Object.fromEntries(r.data.items.map((p) => [p.id, p.short])));
      setBench(r.data.items.filter((p) => p.active && !p.leftAt && p.bench));
    });
  }, []);
  useLive(load, FLOOR_EVENTS);
  async function drop(card, to, slot) {
    if (card.from === to && slot === null && to !== null) return;
    const r = await post(`/tasks/${card.id}/move`, { to, slot, from: card.from });
    if (!r.ok) return flash(r.error.message);
    const u = r.data.undo;
    flash(r.data.message || "Card moved.", u ? async () => {
      const b = await post(`/tasks/${card.id}/move`, { to: u.to, index: u.index, undo: true });
      if (!b.ok) flash(b.error.message);
      load();
    } : undefined);
    load();
  }
  if (!floor) return <div className="loading" data-testid={`${mode}-loading`}>Loading the floor…</div>;
  const trayOk = me.caps.plan;
  const showTray = me.caps.plan || me.caps.addWork;
  const isTmrw = me.tomorrow === addDays(me.today, 1);
  const nextLabel = isTmrw ? "tomorrow" : dayLabel(me.tomorrow).split(" ")[0];
  const nextWord = isTmrw ? "Tomorrow" : dayLabel(me.tomorrow);
  const dayTasks = [...floor.rows.flatMap((r) => r.tasks), ...floor.tray];
  const spotList = clients.filter((c) => dayTasks.some((t) => t.client === c.code));
  const canDrag = me.caps.plan || (me.caps.rearrange && team === me.user.team);
  return (
    <div className="floor" data-testid={`${mode}-page`}>
      <div className="fhead">
        <h1>{mode === "plan" ? <><span className="hl-ink">PACK</span> <span className="hl-mari">{isTmrw ? "TMRW" : nextLabel.toUpperCase()}</span></>
          : <><span className="hl-ink">THE</span> <span className="hl-mari">FLOOR</span></>}</h1>
        <div className="tswitch" role="tablist" aria-label="Team">
          {["design", "video"].map((t) => {
            const c = bench.filter((p) => p.team === t).length;
            return (
              <button key={t} role="tab" aria-selected={team === t} data-team={t} className={`tbtn ${team === t ? "on" : ""}`} onClick={() => setTeam(t)} data-testid={`team-${t}`}>
                <span className="sq">{t === "design" ? "D" : "V"}</span><span><b>{t === "design" ? "Design" : "Video"}</b><small>{c} {c === 1 ? "person" : "people"}</small></span>
              </button>);
          })}
        </div>
        {mode === "floor" && (
          <div className="dswitch" role="tablist" aria-label="Day">
            <button role="tab" aria-selected={day === "today"} className={day === "today" ? "on" : ""} onClick={() => setDay("today")} data-testid="day-today">Today</button>
            <button role="tab" aria-selected={day === "next"} className={day === "next" ? "on" : ""} onClick={() => setDay("next")} data-testid="day-next">{nextWord}</button>
          </div>
        )}
        <label className="spot"><span className="lbl">Spot a client</span>
          <select className="fld" value={spot} onChange={(e) => setSpot(e.target.value)} data-testid="spot-client">
            <option value="">All clients</option>{spotList.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}</select></label>
        <FloorStats rows={floor.rows} team={team} isToday={date === me.today} dayWord={date === me.today ? "today" : isTmrw ? "tomorrow" : dayLabel(date)} />
      </div>
      {mode === "plan" && <PlanBar floor={floor} date={date} today={me.today} label={nextLabel} reload={load} onCalendar={() => setSheet({ kind: "calendar" })} />}
      {floor.hidden && <p className="fnote" data-testid="floor-hidden">{nextWord}'s list is being packed. It lands here when a planner publishes.</p>}
      {!floor.hidden && <LeaveStrip leaves={leaves} rows={floor.rows} date={date} today={me.today} onOpen={(p) => setSheet({ kind: "leave", person: p })} />}
      {!floor.hidden && <HighlightBar tasks={floor.rows.flatMap((r) => r.tasks)} value={hl} onChange={setHl} me={me.user.id} />}
      {!floor.hidden && !canDrag && <p className="fnote" data-testid="floor-lock-note">{me.caps.rearrange ? "You can see the whole floor live. You rearrange only your own team." : "You can see the whole floor live. Planners, management and leads (own team) move cards."}</p>}
      {canDrag && mode === "floor" && <p className="fnote plain">{me.caps.plan ? "Drag cards to reorder or hand them to someone else. Click a card for details and Move to." : "Drag to reorder or hand cards to someone in your team. Estimates and new work stay with the planners."}</p>}
      <div className={`fgrid ${showTray && trayOpen ? "" : "no-tray"}`}>
        <div className="frows" data-team={team}>
          {floor.rows.map((r) => <Row key={r.person.id} me={me} row={r} names={names} pmap={pmap} drop={drop} spot={spot} hl={hl} isToday={date === me.today}
            onBack={async () => { const x = await post("/leaves/end", { person: r.person.id, date }); flash(x.ok ? x.data?.message || `${r.person.short} is back.` : x.error.message); load(); }}
            onAdd={me.caps.plan ? () => setSheet({ kind: "new", person: r.person.id }) : null}
            openCard={(t) => setSheet({ kind: "card", task: t, row: r })} onLeave={() => setSheet({ kind: "leave", person: r.person.id })} />)}
        </div>
        {showTray && !trayOpen && <button className="tray-open" onClick={() => setTrayOpen(true)} aria-label="Open the request tray" data-testid="tray-expand">« Tray <b className="num">{floor.tray.length}</b></button>}
        {showTray && trayOpen && <DropZone className="tray" enabled={trayOk} testid="request-tray" onDrop={(c) => drop(c, null, null)}>
          <div className="tray-h"><span className="lbl">▤ Request tray</span><span className="tray-n num" data-testid="tray-count">{floor.tray.length}</span>
            <button className="tray-x" onClick={() => setTrayOpen(false)} aria-label="Collapse the tray" data-testid="tray-collapse">»</button></div>
          <p className="hint">In the tray for {dayLabel(date)}: new asks, carry-overs and leave hand-offs.
            {trayOk ? " Drag onto a person to assign, drag back here to unassign." : " The planners put these on a list."}</p>
          <button className="btn-ghost sm full" onClick={() => setSheet({ kind: "new" })} data-testid="new-ask-button">+ New ask</button>
          {floor.tray.length === 0 && <p className="empty">Tray is empty. Nice.</p>}
          {floor.tray.map((t) => <FloorCard key={t.id} t={t} names={names} pmap={pmap} dim={(spot && t.client !== spot) || (hl && !matches(hl, t, me.user.id))} draggable={trayOk} from={null} onOpen={() => setSheet({ kind: "card", task: t })} />)}
          <Legend />
        </DropZone>}
      </div>
      {sheet?.kind === "card" && <CardSheet me={me} task={sheet.task} names={names} today={me.today} tomorrow={me.tomorrow}
        onClose={() => setSheet(null)} onChanged={() => { setSheet(null); load(); }} />}
      {sheet?.kind === "leave" && <LeaveSheet rows={floor.rows} person={sheet.person} today={me.today} onClose={() => setSheet(null)}
        onDone={() => { setSheet(null); load(); }} />}
      {sheet?.kind === "new" && <NewCardSheet me={me} rows={floor.rows} clients={clients} date={date} person={sheet.person} onClose={() => setSheet(null)}
        onDone={() => { setSheet(null); load(); }} />}
      {sheet?.kind === "calendar" && <CalendarSheet me={me} onClose={() => setSheet(null)} onChanged={() => { refreshMe(); load(); }} />}
    </div>
  );
}
